import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import CustomerModal, { type Customer } from './CustomerModal'
import { usePagination } from '../../hooks/usePagination'
import PaginationControls from '../../components/PaginationControls'
import { useSyncToSage, formatSyncStatus } from '../../hooks/useSyncToSage'
import { getCachedCount, setCachedCount, invalidateCachedCount } from '../../lib/countCache'
import { useAuth } from '../../contexts/AuthContext'
import { canEditCustomers } from '../../lib/customerPermissions'

interface AddressType { id: string; code: string; description: string }
interface CustomerWithTypes extends Customer {
  address_types: AddressType[]
  sage_contact_id?: string | null
  sage_last_synced_at?: string | null
}

export default function Customers() {
  const navigate = useNavigate()
  // Only the roles the database lets change a company (ADMIN, ACCOUNTS, SALES, OPERATIONS, mirror of the RLS policy) are offered the buttons that
  // change one. For any other role the update would be silently filtered to 0 rows while the screen said 'saved'.
  const { userRoles } = useAuth()
  const canEdit = canEditCustomers(userRoles)
  const [customers, setCustomers] = useState<CustomerWithTypes[]>([])
  const [allTypes, setAllTypes] = useState<AddressType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterActive, setFilterActive] = useState<'true' | 'false' | ''>('true')
  const [filterMissingEori, setFilterMissingEori] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null)
  const [total, setTotal] = useState<number | null>(0)
  const { page, setPage, pageSize, setPageSize, range } = usePagination('customers-list')
  const { syncContact, isSyncingContact } = useSyncToSage()
  const [bulkSyncing, setBulkSyncing] = useState(false)
  const [syncErrors, setSyncErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    supabase.from('address_types').select('id,code,description').eq('is_active', true).order('sort_order')
      .then(({ data }) => setAllTypes(data || []))
  }, [])

  const fetchCustomers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Z35.1: total count only needs a fresh count(*) once per 30s per distinct filter set.
      const countCacheKey = `customers-list:${JSON.stringify({ search, filterType, filterActive, filterMissingEori })}`
      const cachedCount = getCachedCount(countCacheKey)

      let query = supabase
        .from('customers')
        .select('*, customer_address_types(address_type_id, address_types(id, code, description))', cachedCount === undefined ? { count: 'exact' } : undefined)
        .order('name')

      if (search.trim().length >= 1) {
        query = query.or(`name.ilike.%${search}%,code.ilike.%${search}%,contact_email.ilike.%${search}%`)
      }
      if (filterActive !== '') {
        query = query.eq('is_active', filterActive === 'true')
      }
      // Customs needs an EORI on file -- surface companies (e.g. auto-created from a BL that
      // didn't carry one) where it's still blank so it's caught before the declaration step.
      if (filterMissingEori) {
        query = query.or('eori.is.null,eori.eq.')
      }

      // Z56: was a hard .limit(100) with no way to reach rows past it -- real range-based
      // pagination now, same pattern as Jobs/Ports.
      if (range) query = query.range(range[0], range[1])
      else query = query.limit(1000)

      const { data, error: err, count } = await query
      if (err) throw err

      // If filtering by type, filter client-side after join
      let rows = (data || []).map((c: any) => ({
        ...c,
        address_types: (c.customer_address_types || [])
          .map((jt: any) => jt.address_types)
          .filter(Boolean),
      })) as CustomerWithTypes[]

      if (filterType) {
        rows = rows.filter(c => c.address_types.some((t: AddressType) => t.id === filterType))
      }

      setCustomers(rows)
      if (cachedCount !== undefined) {
        setTotal(cachedCount)
      } else {
        setTotal(count ?? 0)
        setCachedCount(countCacheKey, count ?? 0)
      }
    } catch (err: any) {
      setError(err?.message || err?.error_description || String(err) || 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [search, filterType, filterActive, filterMissingEori, range])

  useEffect(() => { setPage(0) }, [search, filterType, filterActive, filterMissingEori, setPage])

  useEffect(() => {
    const timer = setTimeout(fetchCustomers, 300)
    return () => clearTimeout(timer)
  }, [fetchCustomers])

  const openCreate = () => { setEditingCustomer(null); setIsModalOpen(true) }
  const openEdit = (c: Customer) => { setEditingCustomer(c); setIsModalOpen(true) }

  const handleToggleHold = async (c: Customer) => {
    try {
      await supabase.from('customers').update({ is_on_hold: !c.is_on_hold }).eq('id', c.id!)
      fetchCustomers()
    } catch {}
  }

  const handleToggleActive = async (c: Customer) => {
    try {
      await supabase.from('customers').update({ is_active: !c.is_active }).eq('id', c.id!)
      invalidateCachedCount('customers-list') // is_active is a filter -- this changes the counted set
      fetchCustomers()
    } catch {}
  }

  const handleSyncToSage = async (c: CustomerWithTypes) => {
    if (!c.id) return
    const result = await syncContact(c.id)
    setSyncErrors(prev => {
      const next = { ...prev }
      if (result.success) delete next[c.id!]
      else next[c.id!] = result.error || 'Sync failed'
      return next
    })
    if (result.success) fetchCustomers()
  }

  const handleBulkSyncToSage = async () => {
    setBulkSyncing(true)
    for (const c of customers) {
      if (!c.id) continue
      await handleSyncToSage(c)
    }
    setBulkSyncing(false)
  }

  // Colour palette cycling for type chips
  const TYPE_CHIP_COLORS = [
    'bg-blue-100 text-blue-800',
    'bg-green-100 text-green-800',
    'bg-yellow-100 text-yellow-800',
    'bg-purple-100 text-purple-800',
    'bg-orange-100 text-orange-800',
    'bg-pink-100 text-pink-800',
    'bg-indigo-100 text-indigo-800',
    'bg-teal-100 text-teal-800',
    'bg-red-100 text-red-800',
    'bg-cyan-100 text-cyan-800',
    'bg-gray-100 text-gray-800',
  ]
  const typeColorMap = Object.fromEntries(
    allTypes.map((t, i) => [t.id, TYPE_CHIP_COLORS[i % TYPE_CHIP_COLORS.length]])
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Companies</h1>
            <p className="text-gray-600 dark:text-gray-300">Importers, exporters, agents and suppliers: the companies used on jobs, quotes and invoices</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkSyncToSage}
              disabled={bulkSyncing || customers.length === 0}
              title="Sync every company on this page to Sage"
              className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
            >
              {bulkSyncing ? 'Syncing to Sage…' : 'Sync All to Sage'}
            </button>
            {canEdit && (
              <button
                onClick={openCreate}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Company
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search</label>
              <input
                type="text"
                placeholder="Name, code, or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            <div className="min-w-[180px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Role</label>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="">All Roles</option>
                {allTypes.map(t => <option key={t.id} value={t.id}>{t.description}</option>)}
              </select>
            </div>
            <div className="min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
              <select
                value={filterActive}
                onChange={e => setFilterActive(e.target.value as 'true' | 'false' | '')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <label className="flex items-center gap-2 pb-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={filterMissingEori} onChange={e => setFilterMissingEori(e.target.checked)} className="rounded border-gray-300" />
              Missing EORI
            </label>
            <button
              onClick={() => { setSearch(''); setFilterType(''); setFilterActive('true'); setFilterMissingEori(false) }}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Z35.1: controls at both top and bottom of the list */}
          {!error && <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={total} />}
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
              <p className="mt-2 text-gray-600 dark:text-gray-300">Loading...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600 dark:text-red-400">
              <p className="font-medium">Error loading companies</p>
              <p className="text-sm mt-1">{error}</p>
              <button onClick={fetchCustomers} className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200">Retry</button>
            </div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No companies found</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {search || filterType ? 'Try adjusting your filters.' : 'Add your first company to get started.'}
              </p>
              {!search && !filterType && canEdit && (
                <button onClick={openCreate} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Add Company</button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['Code', 'Name', 'Roles', 'Country', 'Contact', 'Payment Terms', 'Currency', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {customers.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900 dark:text-white">
                        <Link to={`/customers/${c.id}`} className="hover:text-blue-600 hover:underline">{c.code}</Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/customers/${c.id}`} className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 hover:underline">{c.name}</Link>
                        {c.is_on_hold && (
                          <span className="inline-flex px-1.5 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-700">ON HOLD</span>
                        )}
                        {/* Z75: quick-reference badges for the Sage sync defaults set in
                            CustomerModal -- only shown when set, since most customers won't have
                            an override. */}
                        {!c.eori?.trim() && (
                          <span
                            className="ml-1.5 inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            title="No EORI on file. Only needed when we clear this company's shipments."
                          >
                            No EORI
                          </span>
                        )}
                        {c.default_sales_ledger_code && (
                          <span
                            className="ml-1.5 inline-flex px-1.5 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                            title="Default Sales Ledger Code"
                          >
                            S:{c.default_sales_ledger_code}
                          </span>
                        )}
                        {c.default_purchase_ledger_code && (
                          <span
                            className="ml-1.5 inline-flex px-1.5 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                            title="Default Purchase Ledger Code"
                          >
                            P:{c.default_purchase_ledger_code}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {c.address_types.length === 0 ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            c.address_types.map((t: AddressType) => (
                              <span key={t.id} className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${typeColorMap[t.id] || 'bg-gray-100 text-gray-800'}`}>
                                {t.description}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{c.country || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{c.contact_name || '—'}</div>
                        {c.contact_email && <div className="text-xs text-gray-500 dark:text-gray-400">{c.contact_email}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{c.payment_terms || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{c.currency}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${c.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-3">
                          {canEdit && (
                            <>
                            <button onClick={() => openEdit(c)} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium">Edit</button>
                            <button onClick={() => handleToggleHold(c)} className={`font-medium ${c.is_on_hold ? 'text-green-600 hover:text-green-800' : 'text-orange-500 hover:text-orange-700'}`}>
                              {c.is_on_hold ? 'Unhold' : 'Hold'}
                            </button>
                            <button onClick={() => handleToggleActive(c)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400">
                              {c.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            </>
                          )}
                          <button
                            onClick={() => handleSyncToSage(c)}
                            disabled={isSyncingContact(c.id || '')}
                            className="text-teal-600 hover:text-teal-800 dark:text-teal-400 font-medium disabled:opacity-50"
                          >
                            {isSyncingContact(c.id || '') ? 'Syncing…' : 'Sync to Sage'}
                          </button>
                        </div>
                        <div className="mt-0.5 text-xs">
                          {c.id && syncErrors[c.id] ? (
                            <span className="text-red-600 dark:text-red-400" title={syncErrors[c.id]}>Sage sync error</span>
                          ) : (
                            <span className="text-gray-400">{formatSyncStatus(c.sage_last_synced_at)}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!error && <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={total} />}
        </div>
      </div>

      <CustomerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        customer={editingCustomer}
        onSaved={({ id, created, addressError }) => {
          invalidateCachedCount('customers-list')
          // A new company opens ITS OWN page. Going back to the list left staff hunting for it among other rows -- and clicking the wrong one to add its address.
          if (created) navigate(`/customers/${id}`, { state: addressError ? { addressNotSaved: addressError } : undefined })
          else fetchCustomers()
        }}
      />
    </div>
  )
}
